import React, { useEffect, useRef, useState } from 'react';

interface Particle {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  color: string;
  size: number;
}

interface FireworksProps {
  isActive: boolean;
  duration?: number;
  onComplete?: () => void;
}

export const MprcFireworks: React.FC<FireworksProps> = ({
  isActive,
  duration = 3000,
  onComplete
}) => {
  const [particles, setParticles] = useState<Particle[]>([]);
  const animationRef = useRef<number | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const colors = [
    '#FF1744', '#E91E63', '#9C27B0', '#673AB7',
    '#3F51B5', '#2196F3', '#03A9F4', '#00BCD4',
    '#009688', '#4CAF50', '#8BC34A', '#CDDC39',
    '#FFEB3B', '#FFC107', '#FF9800', '#FF5722'
  ];

  const createFirework = (x: number, y: number): Particle[] => {
    const particleCount = 40 + Math.random() * 30;
    const newParticles: Particle[] = [];

    for (let i = 0; i < particleCount; i++) {
      const angle = (Math.PI * 2 * i) / particleCount + (Math.random() - 0.5) * 0.6;
      const velocity = 3 + Math.random() * 10;
      const color = colors[Math.floor(Math.random() * colors.length)];

      newParticles.push({
        id: Date.now() + Math.random() + i,
        x,
        y,
        vx: Math.cos(angle) * velocity,
        vy: Math.sin(angle) * velocity,
        life: 70 + Math.random() * 20,
        maxLife: 70 + Math.random() * 20,
        color,
        size: 1.5 + Math.random() * 3.5
      });
    }

    return newParticles;
  };

  const updateParticles = (currentParticles: Particle[]): Particle[] => {
    return currentParticles
      .map(particle => ({
        ...particle,
        x: particle.x + particle.vx,
        y: particle.y + particle.vy,
        vx: particle.vx * 0.98,
        vy: particle.vy * 0.98 + 0.2,
        life: particle.life - 1.5,
        size: particle.size * 0.995
      }))
      .filter(particle => particle.life > 0 && particle.size > 0.2);
  };

  const animate = () => {
    setParticles(prevParticles => updateParticles(prevParticles));
    animationRef.current = requestAnimationFrame(animate);
  };

  const createRandomFirework = () => {
    const x = 100 + Math.random() * (window.innerWidth - 200);
    const y = 50 + Math.random() * (window.innerHeight * 0.4);
    const newFirework = createFirework(x, y);

    setParticles(prevParticles => [...prevParticles, ...newFirework]);
  };

  const stopFireworks = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setParticles([]);
    onComplete?.();
  };

  useEffect(() => {
    if (!isActive) {
      stopFireworks();
      return;
    }

    createRandomFirework();
    setTimeout(() => createRandomFirework(), 50);
    setTimeout(() => createRandomFirework(), 150);

    animate();

    intervalRef.current = setInterval(() => {
      createRandomFirework();
    }, 200);

    timeoutRef.current = setTimeout(() => {
      stopFireworks();
    }, duration);

    return () => {
      stopFireworks();
    };
  }, [isActive]);

  if (particles.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
        zIndex: 9999,
      }}
    >
      <svg
        width="100%"
        height="100%"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          overflow: 'visible'
        }}
      >
        {particles.map(particle => {
          const opacity = Math.max(0, particle.life / particle.maxLife);
          return (
            <circle
              key={particle.id}
              cx={particle.x}
              cy={particle.y}
              r={particle.size}
              fill={particle.color}
              opacity={opacity}
              style={{
                filter: `drop-shadow(0 0 ${particle.size * 1.5}px ${particle.color})`,
              }}
            />
          );
        })}
      </svg>
    </div>
  );
};
